import { useId } from 'react';
import { Box } from '@mui/material';
import { MUSEFLOW_ORANGE } from 'Components/Colors';
import {
  CHOICE_BORDER,
  CHOICE_FILL,
  CHOICE_GROUP_RADIUS_PX,
  CHOICE_SELECTED_FILL,
  choiceOptionSx,
} from 'Components/ChoiceGroup/choiceStyles';
import { onRadioKeyDown, radioTabIndex } from 'Components/ChoiceGroup/radioGroupKeys';
import { SCORE_THEME_LABELS, SCORE_THEMES, getScoreThemePalette } from 'Utils/ScoreTheme';
import type { ScoreTheme } from 'Utils/ScoreTheme';

// The four themes in one segmented row, each with a swatch of its paper. By
// default each segment puts its swatch above its name, and a long name may wrap:
// that holds in any width, and in a browser without container queries (iOS 15).
// Where the picker is wider than this, swatch and name sit side by side.
const SEGMENTS_ROW_FROM_PX = 400.02;

const segmentedGroupSx = {
  display: 'grid',
  gridTemplateColumns: `repeat(${SCORE_THEMES.length}, minmax(0, 1fr))`,
  gap: '4px',
  padding: '4px',
  border: `1px solid ${CHOICE_BORDER}`,
  borderRadius: `${CHOICE_GROUP_RADIUS_PX}px`,
  backgroundColor: CHOICE_FILL,
} as const;

const segmentSx = (selected: boolean) =>
  ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 8px',
    lineHeight: 1.2,
    borderRadius: '8px',
    borderColor: selected ? MUSEFLOW_ORANGE : 'transparent',
    backgroundColor: selected ? CHOICE_SELECTED_FILL : 'transparent',
    flexDirection: 'column',
    gap: '5px',
    fontSize: '13px',
    textAlign: 'center',
    overflowWrap: 'break-word',
    [`@container (min-width: ${SEGMENTS_ROW_FROM_PX}px)`]: {
      flexDirection: 'row',
      gap: '8px',
      fontSize: '14px',
      whiteSpace: 'nowrap',
    },
  }) as const;

/** A small card of the theme's paper with three staff lines in its ink. */
const Swatch = ({ theme }: { theme: ScoreTheme }) => {
  const palette = getScoreThemePalette(theme);
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: '24px',
        height: '16px',
        flexShrink: 0,
        boxSizing: 'border-box',
        borderRadius: '4px',
        border: '1px solid rgba(255, 255, 255, 0.28)',
        backgroundColor: palette.scoreBackground,
        backgroundImage: `repeating-linear-gradient(to bottom, ${palette.staffLineColor} 0 1px, transparent 1px 4px)`,
        backgroundSize: '100% 9px',
        backgroundPosition: '0 3px',
        backgroundRepeat: 'no-repeat',
      }}
    />
  );
};

interface ScoreThemePickerProps {
  value: ScoreTheme;
  onChange: (theme: ScoreTheme) => void;
}

/**
 * Score Theme: the four score colourings as one row of choices, drawn in the
 * same outline, corner and orange selection as "What do you play on?".
 */
export default function ScoreThemePicker({ value, onChange }: ScoreThemePickerProps) {
  const labelId = useId();
  const checkedIndex = SCORE_THEMES.indexOf(value);
  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Box
        id={labelId}
        sx={{
          fontStyle: 'normal',
          fontWeight: '400',
          fontSize: '16px',
          letterSpacing: '0.01em',
          color: '#ECECEC',
          marginBottom: '10px',
        }}
      >
        Score Theme
      </Box>
      <Box role="radiogroup" aria-labelledby={labelId} sx={segmentedGroupSx}>
        {SCORE_THEMES.map((theme, index) => {
          const selected = value === theme;
          return (
            <Box
              key={theme}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={radioTabIndex(index, checkedIndex)}
              onClick={() => onChange(theme)}
              onKeyDown={onRadioKeyDown(index, SCORE_THEMES.length, (target) =>
                onChange(SCORE_THEMES[target]),
              )}
              sx={{ ...choiceOptionSx(selected), ...segmentSx(selected) }}
            >
              <Swatch theme={theme} />
              <span>{SCORE_THEME_LABELS[theme]}</span>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
